<template>
  <article ref="root" class="teach-page" :lang="zh ? 'zh-Hant' : 'en'">
    <!-- 教學頁（2026-10-16）：官網第一個獨立的教學頁。全景課在前，三門 Hahow 錄播課接在後面，新的在前。
         事實與數字只從 src/data/teaching.json 來（Hahow 公開 API 與課程站 /api/overview 的快照），文案是 C 語域：
         第三人稱，他的「我」只住在標日期的引句裡。研究與決定 → Obsidian〈2026-10-16 - 設計 - 官網教學頁…〉。
         課程連結：全景課導到課程網站首頁（那裡每顆報名鈕都是 CR），三門錄播課一律 Hahow CR；都帶 utm、都送 course_click（course-link.mjs）。 -->
    <header class="tp-hero">
      <div class="tp-hero-text">
        <p class="tp-eyebrow">CHE-YU WU · TEACHING · {{ data.totals.since }}–{{ thisYear }}</p>
        <h1>{{ t.title }}<small>{{ t.titleSub }}</small></h1>
        <blockquote class="quote tp-motto">
          <p>{{ t.motto }}</p>
          <cite><router-link :to="local('/writing/2018-effects-course-brewing/')">{{ t.mottoCite }}</router-link></cite>
        </blockquote>
      </div>
      <div class="tp-hero-side">
        <p class="tp-lede">{{ t.lede(fmt(data.totals.students)) }}</p>
        <div class="tp-actions">
          <a class="primary" v-bind="out(pano, 'hero')">{{ t.ctaPano }} ↗︎</a>
          <a href="#courses">{{ t.ctaAll }} ↓</a>
          <router-link :to="local('/contact/')">{{ t.ctaInvite }} →</router-link>
        </div>
      </div>
    </header>

    <!-- 點雲：每一個點是一位同學。滿版，圖說與圖例在下面 -->
    <figure class="tp-field" :aria-label="t.fieldLabel">
      <div class="tp-field-stage">
        <TeachingField :groups="fieldGroups" :seed="20160901" />
      </div>
      <figcaption class="tp-field-cap">
        <p class="tp-field-line">{{ t.fieldCap(fmt(data.totals.students), fmt(pano.students)) }}</p>
        <ul class="tp-legend">
          <li v-for="c in legend" :key="c.slug"><span class="tp-swatch" :style="{ background: c.field.color }" aria-hidden="true"></span>{{ c.short ? c.short[lang] : c.title[lang] }}<b>{{ fmt(c.students) }}</b></li>
        </ul>
        <p class="tp-source">{{ t.fieldSource(dateLong(data.asOf)) }}</p>
      </figcaption>
    </figure>

    <!-- 數字：數字在上、說明在下（dt 在 DOM 前面，用 order 排），跟實驗室頁同一套 -->
    <section class="tp-numbers" :aria-label="t.numbersLabel">
      <dl class="tp-stats">
        <div v-for="s in stats" :key="s.key">
          <dt>{{ s.label }}</dt>
          <dd>{{ s.n }}<small v-if="s.unit">{{ s.unit }}</small></dd>
          <dd class="tp-stat-of">{{ s.of }}</dd>
        </div>
      </dl>
    </section>

    <nav class="tp-nav" :aria-label="t.navLabel">
      <a v-for="n in sectionNav" :key="n.id" :href="'#' + n.id">{{ n.label }}</a>
    </nav>

    <!-- 01 現在：新媒體藝術全景課 -->
    <section id="now" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">01 · NOW</p>
        <h2>{{ t.nowTitle }}</h2>
      </div>
      <div class="tp-now">
        <a class="tp-now-cover" v-bind="out(pano, 'now-cover')" :aria-label="t.ctaNow">
          <img :src="pano.cover" :width="pano.coverW" :height="pano.coverH" :alt="t.coverAlt(pano.title[lang])" loading="lazy" decoding="async" />
          <span v-if="termOn" class="tp-live-badge"><span class="tp-dot on" aria-hidden="true"></span>{{ t.liveBadge }}</span>
        </a>
        <div class="tp-now-text">
          <p class="tp-kicker">2026 · Hahow · {{ t.liveCourse }}</p>
          <h3 class="tp-course-title">{{ pano.title[lang] }}</h3>
          <p class="tp-sub">{{ pano.subtitle[lang] }}</p>
          <p class="tp-body">{{ t.nowBody }}</p>
          <p class="tp-body tp-dissected">
            {{ t.dissectedLead }}
            <template v-for="(w, i) in data.panorama.dissected" :key="w.slug"><router-link :to="local('/work/' + w.slug + '/')">{{ t.workName(w.title[lang]) }}</router-link><span v-if="i < data.panorama.dissected.length - 1">{{ t.sep }}</span></template>{{ t.dissectedTail }}
          </p>
          <dl class="tp-facts">
            <div><dt>{{ t.fWhen }}</dt><dd>{{ data.panorama.schedule[lang] }}</dd></div>
            <div><dt>{{ t.fLessons }}</dt><dd>{{ t.fLessonsV }}</dd></div>
            <div><dt>{{ t.fTools }}</dt><dd>p5.js、OpenProcessing、TouchDesigner、MediaPipe、{{ zh ? 'AI 工具' : 'AI tools' }}</dd></div>
            <div><dt>{{ t.fCohort }}</dt><dd>{{ t.fCohortV(fmt(pano.students)) }}</dd></div>
            <div><dt>{{ t.fHow }}</dt><dd>{{ t.fHowV }}</dd></div>
          </dl>
          <p class="tp-pitch">{{ pano.pitch[lang] }}</p>
          <div class="tp-actions">
            <a class="primary" v-bind="out(pano, 'now')">{{ t.ctaNow }} ↗︎</a>
            <a :href="'https://www.youtube.com/watch?v=' + data.panorama.lessons[0].youtube" target="_blank" rel="noopener">{{ t.ctaReplay }} ↗︎</a>
          </div>
        </div>
      </div>

      <blockquote class="quote tp-quote-wide">
        <p>{{ t.nowQuote }}</p>
        <cite>{{ t.nowQuoteCite }}</cite>
      </blockquote>

      <!-- 十一堂：上過的列時長、互動教具、課堂問答與回放；下一堂亮金點。狀態在瀏覽器裡用台北日期算（預渲染用資料快照日） -->
      <div class="tp-sub-head">
        <h3>{{ t.lessonsTitle }}</h3>
        <p class="tp-note">{{ t.lessonsNote(data.panorama.stats) }}</p>
      </div>
      <div class="tp-parts">
        <section v-for="part in parts" :key="part.id" class="tp-part" :aria-label="part.label">
          <p class="tp-part-label">{{ part.label }}</p>
          <ol class="tp-lessons">
            <li v-for="l in part.lessons" :key="l.n" :class="'is-' + lessonState(l)">
              <span class="tp-lesson-n">{{ String(l.n).padStart(2, '0') }}</span>
              <span class="tp-lesson-date">{{ dateShort(l.date) }}<em v-if="lessonState(l) === 'next'">{{ t.next }}</em></span>
              <span class="tp-lesson-title">{{ l.title[lang] }}</span>
              <span v-if="l.minutes" class="tp-lesson-meta">{{ t.lessonMeta(l) }}<a :href="'https://www.youtube.com/watch?v=' + l.youtube" target="_blank" rel="noopener">{{ t.replay }} ↗︎</a></span>
              <span v-else class="tp-lesson-meta tp-lesson-tool">{{ l.tool }}</span>
            </li>
          </ol>
        </section>
      </div>

      <!-- 白板：直播時邊講邊畫的原圖。圖直角（DESIGN §十八） -->
      <div class="tp-sub-head">
        <h3>{{ t.boardsTitle }}</h3>
        <p class="tp-note">{{ t.boardsNote }}</p>
      </div>
      <div class="tp-boards">
        <figure class="tp-board tp-board-live">
          <img :src="data.panorama.live.src" :width="data.panorama.live.w" :height="data.panorama.live.h" :alt="data.panorama.live.cap[lang]" loading="lazy" decoding="async" />
          <figcaption>{{ data.panorama.live.cap[lang] }}</figcaption>
        </figure>
        <figure v-for="b in data.panorama.whiteboards" :key="b.src" class="tp-board">
          <img :src="b.src" :width="b.w" :height="b.h" :alt="b.cap[lang]" loading="lazy" decoding="async" />
          <figcaption>{{ b.cap[lang] }}</figcaption>
        </figure>
      </div>

      <div class="tp-sub-head">
        <h3>{{ t.worksTitle }}</h3>
        <p class="tp-note">{{ t.worksNote }}</p>
      </div>
      <ul class="tp-works">
        <li v-for="w in data.panorama.works" :key="w.file">
          <a :href="w.link" target="_blank" rel="noopener">
            <img :src="'/static/img/teaching/works/' + w.file" width="560" height="560" :alt="t.workAlt(w)" loading="lazy" decoding="async" />
            <span class="tp-work-meta"><b>{{ w.title }}</b><span>{{ w.who }}・{{ t.lessonHw(w.lesson) }}</span></span>
          </a>
        </li>
      </ul>
      <p class="tp-more"><a v-bind="out(pano, 'works', data.panorama.worksUrl)">{{ t.worksMore(data.panorama.stats.works) }} ↗︎</a></p>

      <ul class="tp-voices tp-voices-now">
        <li v-for="(v, i) in data.panorama.voices" :key="i">
          <p>{{ q(zh ? v.zh : v.en) }}</p>
          <span>{{ t.panoVoiceCite(v.cite[lang]) }}</span>
        </li>
      </ul>
    </section>

    <!-- 02 三門錄播課：新的在前 -->
    <section id="courses" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">02 · COURSES</p>
        <h2>{{ t.coursesTitle }}</h2>
        <p class="tp-note">{{ t.coursesNote(dateLong(data.asOf)) }}</p>
      </div>
      <article v-for="c in recorded" :id="'course-' + c.slug" :key="c.slug" class="tp-course">
        <a class="tp-course-cover" v-bind="out(c, 'course-cover')" :aria-label="t.ctaCourse + '：' + c.title[lang]" tabindex="-1">
          <img :src="c.cover" :width="c.coverW" :height="c.coverH" :alt="t.coverAlt(c.title[lang])" loading="lazy" decoding="async" />
        </a>
        <div class="tp-course-text">
          <p class="tp-kicker">{{ c.year }} · Hahow · {{ t.recorded }}</p>
          <h3 class="tp-course-title">{{ c.title[lang] }}</h3>
          <dl class="tp-course-facts">
            <div><dd>{{ fmt(c.students) }}</dd><dt>{{ t.cStudents }}</dt></div>
            <div><dd>{{ c.rating.toFixed(2) }}</dd><dt>{{ t.cRating(c.ratingCount) }}</dt></div>
            <div><dd>{{ c.hours }}</dd><dt>{{ t.cHours }}</dt></div>
            <div><dd>{{ c.units }}</dd><dt>{{ t.cUnits(c.assignments) }}</dt></div>
          </dl>
          <p class="tp-body">{{ c.story[lang] }}</p>
          <blockquote class="quote tp-course-quote">
            <p>{{ c.quote[lang] }}</p>
            <cite><router-link v-if="c.quote.href" :to="local(c.quote.href)">{{ c.quote.cite[lang] }}</router-link><template v-else>{{ c.quote.cite[lang] }}</template></cite>
          </blockquote>
          <figure class="tp-review">
            <p>{{ q(c.review[lang]) }}</p>
            <figcaption>{{ t.reviewCite(c.review.date) }}</figcaption>
          </figure>
          <div class="tp-actions">
            <a class="primary" v-bind="out(c, 'course-card')">{{ t.ctaCourse }} ↗︎</a>
            <span class="tp-tools-line">{{ c.tools[lang] }}</span>
          </div>
        </div>
      </article>
    </section>

    <!-- 03 一門課是怎麼做出來的 -->
    <section id="making" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">03 · MAKING</p>
        <h2>{{ t.makingTitle }}</h2>
        <p class="tp-note">{{ t.makingNote }}</p>
      </div>
      <ol class="tp-making">
        <li v-for="m in data.making" :key="m.year + m.title.zh">
          <span class="tp-making-year">{{ m.year }}</span>
          <h3>{{ m.title[lang] }}</h3>
          <p>{{ zh ? m.zh : m.en }}</p>
        </li>
      </ol>
      <blockquote class="quote tp-quote-wide">
        <p>{{ t.makingQuote }}</p>
        <cite><router-link :to="local('/writing/2018-effects-course-brewing/')">{{ t.makingQuoteCite }}</router-link></cite>
      </blockquote>
      <div class="tp-sub-head"><h3>{{ t.toolsTitle }}</h3></div>
      <ul class="tp-tools">
        <li v-for="tool in data.tools" :key="tool.key">
          <a :href="tool.key === 'site' ? courseHref(pano, 'tools', lang) : tool.url" target="_blank" rel="noopener" v-bind="tool.key === 'site' ? trackAttrs(pano, 'tools') : {}">
            <b>{{ tool.name[lang] }} ↗︎</b>
            <span>{{ zh ? tool.zh : tool.en }}</span>
            <small>{{ host(tool.url) }}</small>
          </a>
        </li>
      </ul>
    </section>

    <!-- 04 學生怎麼說：Hahow 公開評價 100 則的評價牆（名字照 Hahow 顯示名，哲宇 15:33「那些是公開資訊」） -->
    <section id="voices" class="tp-section">
      <div class="tp-head tp-voices-head">
        <div>
          <p class="tp-eyebrow">04 · VOICES</p>
          <h2>{{ t.voicesTitle }}</h2>
          <p class="tp-note">{{ t.voicesNote(fmt(data.totals.reviews), fmt(wallCount)) }}</p>
        </div>
        <p class="tp-score"><b>{{ ratingRange }}</b><span class="tp-score-stars" aria-hidden="true">★★★★★</span><span>{{ t.voicesScore(fmt(data.totals.reviews)) }}</span></p>
      </div>
      <TeachingReviewWall :lang="lang" :names="courseNames" />
    </section>

    <!-- 05 他怎麼想教學 -->
    <section id="beliefs" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">05 · BELIEFS</p>
        <h2>{{ t.beliefsTitle }}</h2>
      </div>
      <ol class="tp-beliefs">
        <li v-for="(b, i) in data.beliefs" :key="i">
          <blockquote class="quote">
            <p>{{ zh ? b.zh : b.en }}</p>
            <cite>{{ t.beliefCite }}<router-link v-if="b.href" :to="local(b.href)">{{ b.cite[lang] }}</router-link><template v-else>{{ b.cite[lang] }}</template></cite>
          </blockquote>
        </li>
      </ol>
    </section>

    <!-- 06 學校、工作坊與 MONOLAB -->
    <section id="elsewhere" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">06 · BEYOND HAHOW</p>
        <h2>{{ t.elsewhereTitle }}</h2>
      </div>
      <div class="tp-photos">
        <figure v-for="(p, i) in data.photos" :key="p.src" :class="{ big: i === 0 }">
          <img :src="p.src" :width="p.w" :height="p.h" :alt="p.cap[lang]" loading="lazy" decoding="async" />
          <figcaption>{{ p.cap[lang] }}</figcaption>
        </figure>
      </div>
      <div class="tp-elsewhere">
        <article v-for="e in data.elsewhere" :key="e.key">
          <p class="tp-kicker">{{ e.year }}</p>
          <h3>{{ e.title[lang] }}</h3>
          <p>{{ zh ? e.zh : e.en }}</p>
          <!-- TouchDesigner 課程卡（2026-10-18）：兩門課的單元數、時數與學員數（開課快手頁面），主連結到 TD 課程站，購買連結仍到開課快手 -->
          <template v-if="e.td">
            <ul class="tp-td-courses">
              <li v-for="c in e.td.courses" :key="c.slug">
                <b>{{ tdName(e, c.slug) }}</b>
                <span>{{ t.tdFacts(c) }}</span>
              </li>
            </ul>
            <p class="tp-td-note">{{ t.tdNote(e.td.asOf) }}</p>
            <p class="tp-pills tp-td-site">
              <a class="primary" v-bind="out(TD_COURSES.site, 'td-site')">{{ t.tdSite }} ↗︎</a>
            </p>
          </template>
          <p v-if="e.links" class="tp-pills">
            <a v-for="l in e.links" :key="l.slug" v-bind="out(monolabCourse(l), 'monolab')">{{ l.name[lang] }} ↗︎</a>
          </p>
          <p v-if="e.href" class="tp-links">
            <router-link v-if="e.href.startsWith('/')" :to="local(e.href)">{{ t.readMore }} →</router-link>
            <a v-else :href="e.key === 'monolab' ? monolabHome : e.href" target="_blank" rel="noopener">{{ host(e.href) }} ↗︎</a>
          </p>
        </article>
      </div>
    </section>

    <!-- 07 十年 -->
    <section id="timeline" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">07 · TIMELINE</p>
        <h2>{{ t.timelineTitle }}</h2>
      </div>
      <ol class="tp-timeline">
        <li v-for="(e, i) in data.timeline" :key="i">
          <time :datetime="e.date">{{ e.date.replace('-', '.') }}</time>
          <span class="tp-tl-text">
            <a v-if="e.course" v-bind="out(courseBySlug(e.course), 'timeline')">{{ zh ? e.zh : e.en }} ↗︎</a>
            <router-link v-else-if="e.href && e.href.startsWith('/')" :to="local(e.href)">{{ zh ? e.zh : e.en }} →</router-link>
            <a v-else-if="e.href" :href="e.href" target="_blank" rel="noopener">{{ zh ? e.zh : e.en }} ↗︎</a>
            <template v-else>{{ zh ? e.zh : e.en }}</template>
          </span>
        </li>
      </ol>
    </section>

    <!-- 08 延伸閱讀 -->
    <section id="reading" class="tp-section">
      <div class="tp-head">
        <p class="tp-eyebrow">08 · READING</p>
        <h2>{{ t.readingTitle }}</h2>
      </div>
      <ul class="tp-reading">
        <li v-for="r in data.reading" :key="r.href">
          <router-link v-if="r.href.startsWith('/')" :to="local(r.href)"><b>{{ r.title[lang] }}</b><span>{{ r.meta[lang] }}</span></router-link>
          <a v-else :href="r.href" target="_blank" rel="noopener"><b>{{ r.title[lang] }} ↗︎</b><span>{{ r.meta[lang] }}</span></a>
        </li>
      </ul>
    </section>

    <!-- 結尾：課名＋這一期的事實（2026-10-16 哲宇：「這一期還在上。」超 AI，不要口號句） -->
    <section class="tp-closing">
      <p class="tp-eyebrow">HAHOW · 2026</p>
      <h2>{{ pano.title[lang] }}</h2>
      <p class="tp-lede">{{ termOn ? t.closingOn : t.closingAfter }}</p>
      <div class="tp-actions">
        <a class="primary" v-bind="out(pano, 'closing')">{{ t.ctaNow }} ↗︎</a>
        <router-link :to="local('/contact/')">{{ t.ctaInvite }} →</router-link>
      </div>
    </section>
  </article>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import data from '@/data/teaching.json'
import { sitePath } from '@/runtime/site-path.mjs'
import TeachingField from '@/components/teaching/TeachingField.vue'
import TeachingReviewWall from '@/components/teaching/TeachingReviewWall.vue'
import wallData from '@/data/teaching-reviews.json'
import { TD_COURSES, courseHref, observeImpressions, trackCourseClick } from '@/components/teaching/course-link.mjs'

const route = useRoute()
const zh = computed(() => route.path.startsWith('/zh/') || route.path === '/zh')
const lang = computed(() => (zh.value ? 'zh' : 'en'))
const local = (p) => sitePath(p, zh.value)
const root = ref(null)

const courses = data.courses
const courseBySlug = (slug) => courses.find((c) => c.slug === slug)
const pano = courseBySlug('creative-code')
const recorded = courses.filter((c) => c.slug !== 'creative-code')
// 點雲由舊到新畫（2016 的白點在最底，這一期的金點疊在最上面），圖例新的在前
const legend = courses
const fieldGroups = [...courses].reverse().map((c) => ({ key: c.slug, count: c.students, ...c.field }))
// 五星數（data.totals.fiveStars）刷新腳本照樣更新，但頁面不寫：2026-10-16 哲宇對全景課首頁說「不要行銷味道太重」，五星比例被點名
const monolabHome = computed(() => courseHref({ slug: 'monolab', platform: 'monolab', url: 'https://education.monolab.world/' }, 'monolab', lang.value))
const tdName = (e, slug) => e.links.find((l) => l.slug === slug)?.name[lang.value] || slug
const monolabCourse = (l) => ({ slug: l.slug, platform: 'monolab', url: l.url })

// 去處網址＋事件：v-bind="out(course, placement)" 一次給齊 href／target／rel／data-*／onClick
const trackAttrs = (course, placement) => ({ 'data-course': course.slug, 'data-placement': placement, onClick: () => trackCourseClick(course, placement, lang.value) })
const out = (course, placement, url) => {
  const c = url ? { ...course, href: url } : course
  return { href: courseHref(c, placement, lang.value), target: '_blank', rel: 'noopener', ...trackAttrs(course, placement) }
}

const fmt = (n) => Number(n).toLocaleString('en-US')
// 引句外框：中文用直角引號，英文用彎引號
const q = (text) => (zh.value ? `「${text}」` : `“${text}”`)
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, '') } catch { return u } }
const WEEK_ZH = '日一二三四五六', WEEK_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], MON_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const parseDate = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d || 1)) }
const dateShort = (s) => { const d = parseDate(s); return zh.value ? `${String(d.getUTCMonth() + 1).padStart(2, '0')}.${String(d.getUTCDate()).padStart(2, '0')}（${WEEK_ZH[d.getUTCDay()]}）` : `${d.getUTCDate()} ${MON_EN[d.getUTCMonth()]} (${WEEK_EN[d.getUTCDay()]})` }
const dateLong = (s) => { const d = parseDate(s); return zh.value ? `${d.getUTCFullYear()} 年 ${d.getUTCMonth() + 1} 月 ${d.getUTCDate()} 日` : `${d.getUTCDate()} ${MON_EN[d.getUTCMonth()]} ${d.getUTCFullYear()}` }
const ym = (s) => { const [y, m] = s.split('-').map(Number); return zh.value ? `${y} 年 ${m} 月` : `${MON_EN[m - 1]} ${y}` }

// 今天（台北）：預渲染用資料快照日，瀏覽器接手後換成真正的今天，課堂狀態跟著走
const today = ref(data.asOf)
const thisYear = computed(() => today.value.slice(0, 4))
// 這一期還在上嗎：最後一堂（12/22）那天之後，徽章收起來、結尾改說回放與筆記
const termOn = computed(() => today.value <= data.panorama.lessons[data.panorama.lessons.length - 1].date)
const lessonState = (l) => {
  if (l.date < today.value) return 'done'
  const next = data.panorama.lessons.find((x) => x.date >= today.value)
  return next && next.n === l.n ? 'next' : 'later'
}
const PARTS = [
  { id: 'p5', from: 0, to: 4, label: { zh: 'p5.js：在瀏覽器裡畫畫', en: 'p5.js: drawing in the browser' } },
  { id: 'td', from: 5, to: 7, label: { zh: 'TouchDesigner：節點與空間', en: 'TouchDesigner: nodes and space' } },
  { id: 'final', from: 8, to: 10, label: { zh: '整合、發表與之後', en: 'Bringing it together, showing it, and after' } },
]
const parts = computed(() => PARTS.map((p) => ({ id: p.id, label: p.label[lang.value], lessons: data.panorama.lessons.filter((l) => l.n >= p.from && l.n <= p.to) })))
const courseShort = (slug) => { const c = courseBySlug(slug); return (c.short || c.title)[lang.value] }
// 評價牆的課名（課程代號 → 中英短名），只從 teaching.json 來
const courseNames = Object.fromEntries(courses.map((c) => [c.slug, c.short || c.title]))
const wallCount = wallData.reviews.length
// 三門錄播課的評分範圍（4.96–4.98），從 teaching.json 算，刷新腳本更新後跟著變
const ratingRange = (() => { const r = recorded.map((c) => c.rating).filter(Boolean); const lo = Math.min(...r).toFixed(2), hi = Math.max(...r).toFixed(2); return lo === hi ? lo : `${lo}–${hi}` })()

const messages = {
  zh: {
    title: '教學',
    titleSub: 'TEACHING',
    motto: '我既然答應了大家，我就要完整的完成這個東西。……真的燃盡生命，讓大家看見我所相信的世界。',
    mottoCite: '吳哲宇，2018 年 9 月，〈釀造一門結合網頁、設計、數學與特效的線上程式課程〉',
    lede: (n) => `吳哲宇 2016 年在 Hahow 開了第一門線上課，十年間開了四門，上過的同學有 ${n} 位。第一門錄到一半整套推翻重錄，第二門錄了一整年，第三門開錄前先收回 237 份問卷、請 28 位同學試看五週，第四門〈新媒體藝術全景課〉從 2026 年 9 月起每週二晚上直播。`,
    ctaPano: '報名新媒體藝術全景課',
    ctaAll: '看所有課程',
    ctaInvite: '邀請講課與工作坊',
    fieldLabel: '生成畫面：勞倫茲吸引子，每一個點是一位上過吳哲宇 Hahow 課的同學',
    fieldCap: (n, p) => `每一個點是一位在 Hahow 上過他的課的同學，一共 ${n} 個點，沿著勞倫茲吸引子的軌道移動。金色的 ${p} 個是這一期新媒體藝術全景課的同學。滑鼠或手指靠近，點會讓開。`,
    fieldSource: (d) => `人數取自 Hahow 公開資料，${d}。`,
    numbersLabel: '數字',
    navLabel: '本頁章節',
    nav: ['現在', '三門錄播課', '怎麼做一門課', '學生怎麼說', '教學觀', '學校與工作坊', '十年', '延伸閱讀'],
    nowTitle: '現在：新媒體藝術全景課',
    liveBadge: '直播進行中',
    liveCourse: '直播課',
    coverAlt: (n) => `《${n}》課程封面`,
    nowBody: '全景課是吳哲宇在 Hahow 的第四門課，也是第一門直播課。2026 年 9 月 22 日到 12 月 22 日，十一堂課每週二晚上在 YouTube 直播，同學在 Discord 交作業、問問題。課從 p5.js 的第一行程式開始，走過向量與粒子、自然的演算法、聲音與視覺，再到 TouchDesigner、體感互動與音像表演，最後一堂談怎麼跟 AI 一起把創作做下去。',
    dissectedLead: '課堂上拆開來講的，是他自己的作品：',
    dissectedTail: '。',
    sep: '、',
    workName: (n) => `《${n}》`,
    fWhen: '時間', fLessons: '堂數', fLessonsV: '11 堂，10/27、11/3、12/8 不直播，留給期中與期末作品', fTools: '工具', fCohort: '這一期', fCohortV: (n) => `${n} 位同學`, fHow: '上課', fHowV: 'YouTube 直播、Discord 社群、Hahow 回放',
    ctaNow: '到課程網站看全景課',
    ctaReplay: '看第 0 堂直播回放',
    nowQuote: '我希望能夠把我大概這十年來……所有的記憶都留存下來。幫助任何想要入門的人，所以我會不留任何餘力的上好上滿。',
    nowQuoteCite: '吳哲宇，2026 年 9 月 22 日，全景課第 0 堂直播',
    lessonsTitle: '十一堂',
    lessonsNote: (s) => `上過的 ${s.lessonsDone} 堂，下課後都整理成課程網站上的筆記與章節，累計 ${s.widgets} 個互動教具、${s.qa} 題課堂問答。`,
    next: '下一堂',
    replay: '回放',
    lessonMeta: (l) => `${Math.floor(l.minutes / 60)} 小時 ${l.minutes % 60} 分・${l.widgets} 個互動教具・${l.qa} 題問答`,
    boardsTitle: '白板',
    boardsNote: '直播時邊講邊畫的白板，下課後原圖收進課程筆記。',
    worksTitle: '同學做出來的',
    worksNote: '第 0 到 2 堂的作業，作者名是同學在課程社群用的名字。點作品到 OpenProcessing 看程式跑起來。',
    workAlt: (w) => `〈${w.title}〉，${w.who}，第 ${w.lesson} 堂作業`,
    lessonHw: (n) => `第 ${n} 堂作業`,
    worksMore: (n) => `課程網站作品牆：${n} 件`,
    panoVoiceCite: (c) => `全景課・${c}`,
    coursesTitle: '三門錄播課',
    coursesNote: (d) => `新的在前。人數與評價取自 Hahow 公開資料（${d}）。`,
    recorded: '錄播課',
    cStudents: '位同學', cRating: (n) => `評分（${n} 則評價）`, cHours: '小時影片', cUnits: (a) => `個單元、${a} 個作業`,
    reviewCite: (d) => `Hahow 學員評價，${ym(d)}`,
    ctaCourse: '到 Hahow 看這門課',
    makingTitle: '一門課是怎麼做出來的',
    makingNote: '每一條都查得到出處：他當年寫的課程長文、助教紀錄、課程網站與 Hahow 公開資料。',
    makingQuote: '在錄製課程時總是花很多的時間在不斷順內容，不斷重錄，以及一直修改投影片。',
    makingQuoteCite: '吳哲宇，2018 年 9 月，〈釀造一門結合網頁、設計、數學與特效的線上程式課程〉',
    toolsTitle: '為了教而寫的工具',
    voicesTitle: '學生怎麼說',
    voicesNote: (n, w) => `Hahow 三門錄播課的公開評價共 ${n} 則，這裡放其中 ${w} 則，名字照 Hahow 上的顯示名。`,
    voicesScore: (n) => `Hahow 評分・${n} 則評價`,
    beliefsTitle: '他怎麼想教學',
    beliefCite: '吳哲宇，',
    elsewhereTitle: '學校、工作坊與 MONOLAB',
    tdFacts: (c) => `${c.units} 個單元・約 ${c.hours} 小時・${c.students} 位學員`,
    tdNote: (d) => `學員數：${d} 開課快手頁面。`,
    tdSite: '到 TouchDesigner 課程站看單元地圖與筆記',
    readMore: '讀當年的助教日誌',
    timelineTitle: '十年',
    readingTitle: '延伸閱讀',
    closingOn: '11 週完成 8 件作品，打造你的個人創作流。2026 年 9 月 22 日到 12 月 22 日，每週二晚上直播。',
    closingAfter: '2026 年 9 月 22 日到 12 月 22 日這一期的回放在 Hahow，每一堂的筆記、互動教具與同學作品在課程網站。',
  },
  en: {
    title: 'Teaching',
    titleSub: '教學',
    motto: 'Since I promised everyone, I had to see this thing through completely… to genuinely burn through my own life force to let everyone see the world I believe in.',
    mottoCite: 'Che-Yu Wu, September 2018, “Brewing an Online Coding Course That Fuses Web Development, Design, Math, and Visual Effects”',
    lede: (n) => `Che-Yu Wu opened his first online course on Hahow in 2016. Ten years and four courses later, ${n} students have taken them. He scrapped the first course halfway through recording and started again, spent a full year recording the second, collected 237 survey responses and ran a five-week trial with 28 students before recording the third, and since September 2026 has taught the fourth, New Media Art Panorama, live every Tuesday evening. The courses are taught in Mandarin Chinese.`,
    ctaPano: 'New Media Art Panorama',
    ctaAll: 'All courses',
    ctaInvite: 'Invite him to teach',
    fieldLabel: 'Generative image: a Lorenz attractor in which each dot is one student who has taken Che-Yu Wu’s courses on Hahow',
    fieldCap: (n, p) => `Each dot is one student who has taken one of his courses on Hahow: ${n} dots in all, moving along the orbits of a Lorenz attractor. The ${p} gold dots are this term’s New Media Art Panorama class. Move a cursor or finger close and the dots make way.`,
    fieldSource: (d) => `Student counts from Hahow’s public data, ${d}.`,
    numbersLabel: 'Numbers',
    navLabel: 'Sections on this page',
    nav: ['Now', 'Recorded courses', 'Making a course', 'Students', 'Beliefs', 'Schools and workshops', 'Ten years', 'Reading'],
    nowTitle: 'Now: New Media Art Panorama',
    liveBadge: 'Live this term',
    liveCourse: 'live course',
    coverAlt: (n) => `Course cover: ${n}`,
    nowBody: 'Panorama is Che-Yu Wu’s fourth course on Hahow and his first taught live. From 22 September to 22 December 2026, eleven lessons stream on YouTube every Tuesday evening, while students hand in work and ask questions on Discord. It starts with the first line of p5.js and moves through vectors and particles, algorithms of nature, sound and image, then TouchDesigner, body tracking and audiovisual performance. The last lesson is about carrying a creative practice forward with AI.',
    dissectedLead: 'In class he takes apart his own works: ',
    dissectedTail: '.',
    sep: ', ',
    workName: (n) => n,
    fWhen: 'When', fLessons: 'Lessons', fLessonsV: '11; no stream on 27 Oct, 3 Nov and 8 Dec, kept for midterm and final projects', fTools: 'Tools', fCohort: 'This term', fCohortV: (n) => `${n} students`, fHow: 'Format', fHowV: 'YouTube live, a Discord community, replays on Hahow',
    ctaNow: 'Visit the course website',
    ctaReplay: 'Watch the lesson 0 replay',
    nowQuote: 'I hope to keep what I’ve gathered over roughly these ten years… every memory of it. To help anyone who wants to get started, so I’ll put everything I have into every class.',
    nowQuoteCite: 'Che-Yu Wu, 22 September 2026, Panorama lesson 0, live (translated from Chinese)',
    lessonsTitle: 'Eleven lessons',
    lessonsNote: (s) => `Each of the ${s.lessonsDone} lessons taught so far has been turned into notes and chapters on the course website, with ${s.widgets} interactive teaching tools and ${s.qa} answered questions in all.`,
    next: 'Next',
    replay: 'Replay',
    lessonMeta: (l) => `${Math.floor(l.minutes / 60)} h ${l.minutes % 60} min · ${l.widgets} interactive tools · ${l.qa} questions`,
    boardsTitle: 'Whiteboards',
    boardsNote: 'Drawn live while he teaches; the originals go into the course notes afterwards.',
    worksTitle: 'What students made',
    worksNote: 'Assignments from lessons 0 to 2, credited with the names students use in the course community. Click a piece to see it run on OpenProcessing.',
    workAlt: (w) => `“${w.title}” by ${w.who}, lesson ${w.lesson} assignment`,
    lessonHw: (n) => `lesson ${n}`,
    worksMore: (n) => `All ${n} works on the course website`,
    panoVoiceCite: (c) => `Panorama · ${c}`,
    coursesTitle: 'Three recorded courses',
    coursesNote: (d) => `Newest first. Student numbers and ratings from Hahow’s public data (${d}). Taught in Mandarin Chinese.`,
    recorded: 'recorded course',
    cStudents: 'students', cRating: (n) => `rating (${n} reviews)`, cHours: 'hours of video', cUnits: (a) => `units, ${a} assignments`,
    reviewCite: (d) => `Hahow student review, ${ym(d)} (translated from Chinese)`,
    ctaCourse: 'View on Hahow',
    makingTitle: 'How a course gets made',
    makingNote: 'Every line here has a source: the essays he wrote about the courses at the time, his teaching-assistant log, the course website and Hahow’s public data.',
    makingQuote: 'So when recording the course, I always spent a huge amount of time endlessly smoothing out the content, re-recording again and again, and constantly revising the slides.',
    makingQuoteCite: 'Che-Yu Wu, September 2018, “Brewing an Online Coding Course That Fuses Web Development, Design, Math, and Visual Effects”',
    toolsTitle: 'Tools he built to teach',
    voicesTitle: 'What students say',
    voicesNote: (n, w) => `${w} of the ${n} public reviews of the three recorded courses on Hahow, in the original Chinese, with the names students show on Hahow.`,
    voicesScore: (n) => `Hahow rating · ${n} reviews`,
    beliefsTitle: 'How he thinks about teaching',
    beliefCite: 'Che-Yu Wu, ',
    elsewhereTitle: 'Schools, workshops and MONOLAB',
    tdFacts: (c) => `${c.units} units · about ${c.hours} hours · ${c.students} students`,
    tdNote: (d) => `Student counts: MONOLAB Education course pages, ${d}.`,
    tdSite: 'See the unit map and notes on the course site',
    readMore: 'Read his TA journal from the time',
    timelineTitle: 'Ten years',
    readingTitle: 'Further reading',
    closingOn: 'Make 8 works in 11 weeks and build your own creative flow. Live every Tuesday evening, 22 September to 22 December 2026.',
    closingAfter: 'Replays of the 22 September – 22 December 2026 term are on Hahow; the notes, interactive tools and student works for every lesson are on the course website.',
  },
}
const t = computed(() => messages[lang.value])
const SECTION_IDS = ['now', 'courses', 'making', 'voices', 'beliefs', 'elsewhere', 'timeline', 'reading']
const sectionNav = computed(() => SECTION_IDS.map((id, i) => ({ id, label: `${String(i + 1).padStart(2, '0')} ${t.value.nav[i]}` })))

const stats = computed(() => {
  const z = zh.value, T = data.totals
  return [
    { key: 'students', n: fmt(T.students), label: z ? '位同學上過他的 Hahow 課' : 'students on Hahow', of: z ? '四門課合計' : 'across four courses' },
    { key: 'reviews', n: fmt(T.reviews), label: z ? '則 Hahow 評價' : 'Hahow reviews', of: z ? '三門錄播課，平均 4.96 到 4.98' : 'three recorded courses, averages of 4.96 to 4.98' },
    { key: 'hours', n: T.videoHours, unit: z ? '小時' : 'h', label: z ? '課程影片' : 'of course video', of: z ? '四門課在 Hahow 上的影片，全景課的回放還在增加' : 'on Hahow, still growing with Panorama replays' },
    { key: 'yt', n: T.youtubeVideos, unit: z ? '支' : '', label: z ? 'YouTube 教學影片' : 'teaching videos on YouTube', of: z ? `共 ${T.youtubeHours} 小時，頻道「哲宇寇汀吧」` : `${T.youtubeHours} hours on his channel` },
    { key: 'ta', n: T.taSessions, unit: z ? '次' : '', label: z ? '紐約大學一對一助教輔導' : 'one-to-one TA sessions at NYU', of: z ? '2018 到 2019 年，兩個學期' : 'two semesters, 2018–2019' },
    { key: 'years', n: Number(thisYear.value) - T.since, unit: z ? '年' : ' years', label: z ? `從 ${T.since} 年的第一門課算起` : `since his first course in ${T.since}`, of: '' },
  ]
})

// 結構化資料：集合頁＋四門課（每門課的網址指回本頁的錨點：Google 的課程清單要求清單項目在同一個網域）
useHead({
  script: computed(() => [{
    key: 'teaching-courses-jsonld',
    type: 'application/ld+json',
    innerHTML: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: zh.value ? '吳哲宇的線上課程' : 'Online courses by Che-Yu Wu',
      itemListElement: courses.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://cheyuwu.com${local('/teaching/')}#${c.slug === 'creative-code' ? 'now' : 'course-' + c.slug}`,
        item: {
          '@type': 'Course',
          name: c.title.zh,
          alternateName: c.title.en,
          description: c.subtitle ? c.subtitle[lang.value] : c.story[lang.value].slice(0, 160),
          inLanguage: 'zh-Hant',
          provider: { '@type': 'Organization', name: 'Hahow', sameAs: 'https://hahow.in/' },
          instructor: { '@id': 'https://cheyuwu.com/#person' },
        },
      })),
    }),
  }]),
})

let stopImpressions = () => {}
onMounted(() => {
  try { today.value = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Taipei', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()) } catch { /* 舊瀏覽器：留著快照日 */ }
  stopImpressions = observeImpressions(root.value, (slug) => courseBySlug(slug) || Object.values(TD_COURSES).find((c) => c.slug === slug) || { slug, platform: 'monolab' }, lang.value)
})
onBeforeUnmount(() => stopImpressions())
</script>

<style scoped>
/* 教學頁：跟實驗室、關於同一套（--editorial-w、Noto Serif TC 大標、eyebrow、無框膠囊、--bg1 面）。
   圖片一律直角（DESIGN.md §十八）。動態只有頁首點雲與直播金點，減少動態時都停。 */
.teach-page {
  --tp-warm: #b7ad9b;
  max-width: var(--editorial-w, 1560px);
  margin: auto;
  padding: 64px 40px 0;
  color: var(--ink);
  font-family: var(--font-body);
  text-align: left;
  overflow-wrap: anywhere;
}
h1, h2, h3 { font-family: var(--font-display); font-weight: 400; }
a { color: var(--gold); text-decoration: none; transition: var(--t-hover); }
a:hover { color: var(--ink); }
a:focus-visible { outline: 2px solid var(--gold); outline-offset: 4px; }
.tp-eyebrow { color: var(--tp-warm); font-size: 13px; letter-spacing: 0.08em; margin: 0; }
.tp-kicker { margin: 0 0 8px; color: var(--tp-warm); font-size: 13px; letter-spacing: 0.04em; }
.tp-note { color: var(--ink-2); font-size: 15px; line-height: 1.8; margin: 0; max-width: 46em; }
.tp-body { font-size: 17px; line-height: 1.95; color: var(--ink); margin: 0 0 20px; max-width: 38em; }
.tp-source { margin: 0; color: var(--ink-3); font-size: 13px; line-height: 1.6; }

/* ── 頁首：左邊大標與引句，右邊導言與按鈕 ── */
.tp-hero { display: grid; grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr); gap: 72px; align-items: end; margin-bottom: 56px; }
.tp-hero h1 { font-size: clamp(72px, 9vw, 136px); line-height: 1; margin: 24px 0 8px; }
.tp-hero h1 small { display: block; margin-top: 16px; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 13px; letter-spacing: 0.16em; color: var(--ink-3); }
.tp-motto { font-size: clamp(20px, 1.9vw, 26px); line-height: 1.75; margin: 40px 0 0; max-width: 25em; text-wrap: pretty; }
.tp-motto cite a { color: var(--ink-3); }
.tp-motto cite a:hover { color: var(--gold); }
.tp-lede { color: var(--ink-2); font-size: 17px; line-height: 1.95; max-width: 34em; margin: 0 0 28px; }
.tp-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
.tp-actions a {
  display: inline-flex; align-items: center; justify-content: center; min-height: 44px; padding: 10px 20px;
  border: 0; border-radius: var(--r-pill); color: var(--ink); background: var(--tint-2); font-size: 15px; transition: var(--t-hover);
}
.tp-actions a:hover { background: var(--tint-3); color: var(--ink); }
.tp-actions a.primary { background: #c5af7f; color: #080808; }
.tp-actions a.primary:hover { background: var(--gold); color: #000; }

/* ── 點雲：滿版（突破 editorial 寬度），桌機 16:7、手機 4:5 ── */
.tp-field { margin: 0 0 0 calc(50% - 50vw); width: 100vw; }
.tp-field-stage { height: min(56vw, 860px); min-height: 420px; background: #000; }
.tp-field-cap { max-width: var(--editorial-w, 1560px); margin: 0 auto; padding: 18px 40px 0; display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); gap: 12px 48px; align-items: start; }
.tp-field-line { margin: 0; color: var(--ink-2); font-size: 15px; line-height: 1.8; max-width: 40em; }
.tp-legend { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 24px; font-size: 13px; color: var(--ink-2); }
.tp-legend li { display: flex; align-items: baseline; gap: 8px; min-width: 0; }
.tp-legend b { margin-left: auto; font-weight: 400; font-variant-numeric: tabular-nums; color: var(--ink); }
.tp-swatch { flex: none; width: 8px; height: 8px; border-radius: 50%; transform: translateY(-1px); }
.tp-field-cap .tp-source { grid-column: 1 / -1; }

/* ── 數字列 ── */
.tp-numbers { padding: 56px 0 40px; }
.tp-stats { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 28px 24px; margin: 0; }
.tp-stats div { display: flex; flex-direction: column; gap: 8px; min-width: 0; }
.tp-stats dd { order: -1; margin: 0; font-family: var(--font-display); font-size: clamp(34px, 3.4vw, 54px); line-height: 1; font-variant-numeric: tabular-nums; }
.tp-stats dd small { font-size: 0.4em; margin-left: 4px; color: var(--ink-2); }
.tp-stats dt { color: var(--ink); font-size: 14px; font-weight: 400; line-height: 1.5; }
.tp-stats .tp-stat-of { order: 1; font-family: var(--font-body); font-size: 13px; line-height: 1.5; color: var(--ink-3); }

.tp-nav { display: flex; flex-wrap: wrap; gap: 4px 32px; margin: 0; padding: 8px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
.tp-nav a { display: flex; align-items: center; min-height: 44px; color: #c5af7f; font-size: 15px; }
.tp-nav a:hover { color: var(--ink); }

/* ── 章節 ── */
.tp-section { padding: 96px 0; border-bottom: 1px solid var(--line); scroll-margin-top: 90px; content-visibility: auto; contain-intrinsic-size: auto var(--ci, 1200px); }
#now { --ci: 3600px; }
#courses { --ci: 2600px; }
#making { --ci: 1500px; }
/* 評價牆是滿版的：content-visibility:auto 會帶繪製裁切，把突破內容寬度的那幾排切掉，這一節不用它 */
#voices { content-visibility: visible; contain-intrinsic-size: none; }
#beliefs { --ci: 1000px; }
#elsewhere { --ci: 1500px; }
#timeline { --ci: 1300px; }
#reading { --ci: 500px; }
.tp-head { margin-bottom: 44px; max-width: 48em; }
.tp-head h2 { font-size: clamp(34px, 4.2vw, 58px); line-height: 1.2; margin: 12px 0 16px; }
.tp-sub-head { margin: 72px 0 24px; max-width: 46em; }
.tp-sub-head h3 { font-size: clamp(24px, 2.2vw, 32px); margin: 0 0 8px; }
.tp-more { margin: 20px 0 0; }
.tp-more a { display: inline-flex; align-items: center; min-height: 44px; }

/* 01 現在 */
.tp-now { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 56px; align-items: start; }
.tp-now-cover { position: relative; display: block; background: var(--bg1); }
.tp-now-cover img { display: block; width: 100%; height: auto; }
.tp-live-badge { position: absolute; left: 14px; top: 14px; display: inline-flex; align-items: center; gap: 8px; padding: 6px 12px; border-radius: var(--r-pill); background: rgba(0, 0, 0, 0.66); color: var(--ink); font-size: 13px; -webkit-backdrop-filter: blur(8px); backdrop-filter: blur(8px); }
.tp-dot { flex: none; width: 7px; height: 7px; border-radius: 50%; background: var(--ink-4); }
.tp-dot.on { background: var(--gold); animation: tp-pulse 2.4s var(--ease-out) infinite; }
@keyframes tp-pulse { 0% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0.5) } 70% { box-shadow: 0 0 0 9px rgba(217, 194, 103, 0) } 100% { box-shadow: 0 0 0 0 rgba(217, 194, 103, 0) } }
.tp-course-title { font-size: clamp(28px, 2.6vw, 40px); line-height: 1.25; margin: 0 0 10px; }
.tp-sub { margin: 0 0 24px; color: var(--ink-2); font-size: 17px; line-height: 1.7; }
.tp-dissected a { color: var(--ink); border-bottom: 1px solid var(--line-2); }
.tp-dissected a:hover { color: var(--gold); border-color: var(--gold); }
.tp-facts { margin: 8px 0 24px; display: grid; gap: 0; }
.tp-facts div { display: grid; grid-template-columns: 6em minmax(0, 1fr); gap: 16px; padding: 11px 0; border-top: 1px solid var(--line); }
.tp-facts dt { color: var(--ink-3); font-size: 14px; }
.tp-facts dd { margin: 0; font-size: 15px; line-height: 1.6; }
.tp-pitch { margin: 0 0 20px; font-family: var(--font-display); font-size: 19px; line-height: 1.7; color: var(--ink); }
.tp-quote-wide { max-width: 34em; margin: 80px 0 0; font-size: clamp(20px, 1.8vw, 25px); }

.tp-parts { display: grid; grid-template-columns: 5fr 3fr 3fr; gap: 32px; }
.tp-part-label { margin: 0 0 12px; color: var(--tp-warm); font-size: 13px; letter-spacing: 0.04em; }
.tp-lessons { list-style: none; margin: 0; padding: 0; }
.tp-lessons li { display: grid; grid-template-columns: 2.4em minmax(0, 1fr); gap: 2px 12px; padding: 14px 0; border-top: 1px solid var(--line); }
.tp-lesson-n { grid-row: span 3; font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 13px; color: var(--ink-3); padding-top: 3px; }
.tp-lesson-date { font-size: 13px; color: var(--ink-3); font-variant-numeric: tabular-nums; }
.tp-lesson-date em { font-style: normal; margin-left: 10px; padding: 1px 8px; border-radius: var(--r-pill); background: var(--gold-tint); color: var(--gold); }
.tp-lesson-title { font-family: var(--font-display); font-size: 18px; line-height: 1.45; }
.tp-lesson-meta { font-size: 13px; line-height: 1.6; color: var(--ink-2); }
.tp-lesson-meta a { margin-left: 10px; }
.tp-lesson-tool { color: var(--ink-3); }
.tp-lessons li.is-later .tp-lesson-title { color: var(--ink-2); }
.tp-lessons li.is-next .tp-lesson-n { color: var(--gold); }

.tp-boards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 32px 20px; }
.tp-board { margin: 0; min-width: 0; }
.tp-board img { display: block; width: 100%; height: auto; background: #fff; }
.tp-board-live { grid-column: span 2; grid-row: span 2; }
.tp-board figcaption { margin-top: 10px; color: var(--ink-3); font-size: 13px; line-height: 1.6; }

.tp-works { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 6px; }
.tp-works li { position: relative; aspect-ratio: 1 / 1; background: var(--bg1); }
.tp-works a { position: absolute; inset: 0; display: block; overflow: hidden; color: var(--ink); }
.tp-works img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform var(--dur-scene) var(--ease-out); }
.tp-works a:hover img, .tp-works a:focus-visible img { transform: scale(1.03); }
.tp-works a:focus-visible { outline-offset: -2px; }
.tp-work-meta {
  position: absolute; left: 0; right: 0; bottom: 0; padding: 28px 10px 8px; display: flex; flex-direction: column; gap: 2px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.84)); opacity: 0; transform: translateY(6px);
  transition: opacity var(--dur-quick) var(--ease-out), transform var(--dur-base) var(--ease-out);
}
.tp-works a:hover .tp-work-meta, .tp-works a:focus-visible .tp-work-meta { opacity: 1; transform: none; }
.tp-work-meta b { font-weight: 500; font-size: 13px; line-height: 1.35; }
.tp-work-meta span { font-size: 11.5px; color: var(--ink-2); }
@media (hover: none) { .tp-work-meta { opacity: 1; transform: none; } }

.tp-voices { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.tp-voices-now { margin-top: 56px; grid-template-columns: repeat(4, minmax(0, 1fr)); }
.tp-voices li { display: flex; flex-direction: column; justify-content: space-between; gap: 18px; padding: 26px 26px 22px; border-radius: var(--r-lg); background: var(--bg1); }
.tp-voices p { margin: 0; font-family: var(--font-display); font-size: 18px; line-height: 1.75; }
.tp-voices span { color: var(--ink-3); font-size: 13px; line-height: 1.5; }

/* 02 三門錄播課：封面 5、文字 7；一門一列 */
.tp-course { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); gap: 56px; padding: 56px 0; border-top: 1px solid var(--line); scroll-margin-top: 90px; }
.tp-course:first-of-type { border-top: 0; padding-top: 0; }
.tp-course-cover { display: block; align-self: start; position: sticky; top: 110px; background: var(--bg1); }
.tp-course-cover img { display: block; width: 100%; height: auto; transition: opacity var(--dur-quick) var(--ease-out); }
.tp-course-cover:hover img { opacity: 0.88; }
.tp-course-facts { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin: 18px 0 28px; padding: 18px 0; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
.tp-course-facts div { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.tp-course-facts dd { margin: 0; font-family: var(--font-display); font-size: clamp(26px, 2.2vw, 34px); line-height: 1; font-variant-numeric: tabular-nums; }
.tp-course-facts dt { color: var(--ink-3); font-size: 13px; line-height: 1.45; }
.tp-course-quote { font-size: 1.15em; max-width: 30em; }
.tp-course-quote cite a { color: var(--ink-3); }
.tp-course-quote cite a:hover { color: var(--gold); }
.tp-review { margin: 0 0 28px; padding: 22px 24px; border-radius: var(--r-lg); background: var(--bg1); max-width: 40em; }
.tp-review p { margin: 0 0 10px; font-size: 16px; line-height: 1.85; color: var(--ink); }
.tp-review figcaption { color: var(--ink-3); font-size: 13px; }
.tp-tools-line { color: var(--ink-3); font-size: 13px; margin-left: 8px; }

/* 04 評價牆的頁首：左邊標題、右邊評分 */
.tp-voices-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 16px 48px; max-width: none; }
.tp-voices-head > div { max-width: 48em; }
.tp-score { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 12px; margin: 0; color: var(--ink-3); font-size: 13px; }
.tp-score b { font-family: var(--font-display); font-weight: 400; font-size: clamp(30px, 3vw, 44px); line-height: 1; color: var(--ink); font-variant-numeric: tabular-nums; }
.tp-score-stars { color: var(--gold); letter-spacing: 0.14em; font-size: 14px; }

/* 03 怎麼做一門課 */
.tp-making { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.tp-making li { padding: 28px 28px 26px; border-radius: var(--r-lg); background: var(--bg1); }
.tp-making-year { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12.5px; letter-spacing: 0.04em; color: var(--gold); }
.tp-making h3 { font-size: 24px; margin: 10px 0 12px; }
.tp-making p { margin: 0; color: var(--ink-2); font-size: 15.5px; line-height: 1.85; }
.tp-tools { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.tp-tools a { display: flex; flex-direction: column; gap: 8px; height: 100%; padding: 24px 26px; border-radius: var(--r-lg); background: var(--bg1); color: var(--ink); transition: var(--t-hover); }
.tp-tools a:hover { background: var(--bg2); }
.tp-tools a:hover b { color: var(--gold); }
.tp-tools b { font-family: var(--font-display); font-weight: 400; font-size: 21px; transition: var(--t-hover); }
.tp-tools span { color: var(--ink-2); font-size: 15px; line-height: 1.7; }
.tp-tools small { margin-top: auto; color: var(--ink-3); font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 12px; }

/* 05 教學觀：兩欄引句 */
.tp-beliefs { list-style: none; margin: 0; padding: 0; columns: 2; column-gap: 72px; }
.tp-beliefs li { break-inside: avoid; margin: 0 0 12px; }
.tp-beliefs .quote { margin: 0 0 40px; font-size: clamp(19px, 1.6vw, 23px); }
.tp-beliefs cite a { color: var(--ink-3); }
.tp-beliefs cite a:hover { color: var(--gold); }

/* 06 學校、工作坊與 MONOLAB */
.tp-photos { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 1fr); grid-template-rows: auto auto; gap: 20px; margin-bottom: 56px; }
.tp-photos figure { margin: 0; min-width: 0; }
.tp-photos figure.big { grid-row: span 2; }
.tp-photos img { display: block; width: 100%; height: auto; }
.tp-photos figcaption { margin-top: 10px; color: var(--ink-3); font-size: 13px; line-height: 1.6; }
.tp-elsewhere { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 48px 64px; }
.tp-elsewhere article { min-width: 0; padding-top: 22px; border-top: 1px solid var(--line); }
.tp-elsewhere h3 { font-size: 24px; line-height: 1.35; margin: 0 0 12px; }
.tp-elsewhere p { margin: 0 0 14px; color: var(--ink-2); font-size: 15.5px; line-height: 1.85; }
.tp-pills { display: flex; flex-wrap: wrap; gap: 8px; }
.tp-pills a { display: inline-flex; align-items: center; min-height: 38px; padding: 6px 14px; border-radius: var(--r-pill); background: var(--tint-2); color: var(--ink); font-size: 14px; }
.tp-pills a:hover { background: var(--tint-3); }
.tp-td-courses { list-style: none; margin: 0 0 10px; padding: 0; }
.tp-td-courses li { display: flex; flex-direction: column; gap: 2px; padding: 10px 0; border-top: 1px solid var(--line); }
.tp-td-courses b { font-weight: 500; font-size: 16px; color: var(--ink); }
.tp-td-courses span, .tp-elsewhere .tp-td-note { color: var(--ink-3); font-size: 13.5px; line-height: 1.6; }
.tp-elsewhere .tp-td-note { margin: 0 0 14px; }
.tp-td-site a.primary { background: #c5af7f; color: #080808; }
.tp-td-site a.primary:hover { background: var(--gold); color: #000; }
.tp-links a { display: inline-flex; align-items: center; min-height: 40px; }

/* 07 十年：日期等寬、文字一行 */
.tp-timeline { list-style: none; margin: 0; padding: 0; columns: 2; column-gap: 64px; }
.tp-timeline li { break-inside: avoid; display: grid; grid-template-columns: 6.2em minmax(0, 1fr); gap: 16px; padding: 12px 0; border-top: 1px solid var(--line); }
.tp-timeline time { font-family: ui-monospace, 'SF Mono', Menlo, monospace; font-size: 13px; color: var(--ink-3); padding-top: 3px; font-variant-numeric: tabular-nums; }
.tp-tl-text { font-size: 15.5px; line-height: 1.7; color: var(--ink); }
.tp-tl-text a { color: var(--ink); }
.tp-tl-text a:hover { color: var(--gold); }

/* 08 延伸閱讀 */
.tp-reading { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.tp-reading a { display: flex; flex-direction: column; gap: 10px; height: 100%; padding: 24px 26px; border-radius: var(--r-lg); background: var(--bg1); color: var(--ink); transition: var(--t-hover); }
.tp-reading a:hover { background: var(--bg2); }
.tp-reading a:hover b { color: var(--gold); }
.tp-reading b { font-family: var(--font-display); font-weight: 400; font-size: 19px; line-height: 1.5; transition: var(--t-hover); }
.tp-reading span { color: var(--ink-3); font-size: 13px; }

/* 結尾 */
.tp-closing { padding: 120px 0 140px; text-align: left; }
.tp-closing h2 { font-size: clamp(36px, 4.6vw, 64px); line-height: 1.2; margin: 14px 0 20px; }

/* ── 平板 ── */
@media (max-width: 1199px) {
  .tp-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 36px 24px; }
  .tp-parts { grid-template-columns: 1fr; gap: 28px; }
  .tp-voices-now { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tp-making, .tp-tools, .tp-reading { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tp-works { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
@media (max-width: 991px) {
  .teach-page { padding: 40px 24px 0; }
  .tp-hero { grid-template-columns: 1fr; gap: 32px; margin-bottom: 40px; }
  .tp-field-cap { grid-template-columns: 1fr; padding: 16px 24px 0; }
  .tp-field-stage { height: 62vw; min-height: 300px; }
  .tp-now { grid-template-columns: 1fr; gap: 32px; }
  .tp-course { grid-template-columns: 1fr; gap: 28px; }
  .tp-course-cover { position: static; }
  .tp-boards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tp-board-live { grid-column: 1 / -1; grid-row: auto; }
  .tp-voices { grid-template-columns: 1fr 1fr; }
  .tp-beliefs, .tp-timeline { columns: 1; }
  .tp-elsewhere { grid-template-columns: 1fr; gap: 40px; }
  .tp-photos { grid-template-columns: 1fr 1fr; }
  .tp-photos figure.big { grid-column: 1 / -1; grid-row: auto; }
  .tp-section { padding: 72px 0; }
}
/* ── 手機 ── */
@media (max-width: 640px) {
  .teach-page { padding: 32px 16px 0; }
  .tp-hero h1 { font-size: 64px; }
  .tp-field-cap { padding: 14px 16px 0; }
  .tp-field-stage { height: 80vw; min-height: 0; max-height: 520px; }
  .tp-legend { grid-template-columns: 1fr; }
  .tp-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px 16px; }
  .tp-nav { gap: 0 20px; }
  .tp-nav a { min-height: 40px; font-size: 14px; }
  .tp-section { padding: 56px 0; }
  .tp-sub-head { margin-top: 56px; }
  .tp-facts div { grid-template-columns: 4.5em minmax(0, 1fr); gap: 12px; }
  .tp-course-facts { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 12px; }
  .tp-boards { grid-template-columns: 1fr; }
  .tp-works { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 4px; }
  .tp-work-meta { padding: 18px 6px 5px; }
  .tp-work-meta b { font-size: 11.5px; }
  .tp-work-meta span { display: none; }
  .tp-voices, .tp-voices-now, .tp-making, .tp-tools, .tp-reading { grid-template-columns: 1fr; }
  .tp-voices li, .tp-making li { padding: 22px 20px; }
  .tp-photos { grid-template-columns: 1fr; }
  .tp-timeline li { grid-template-columns: 5.4em minmax(0, 1fr); gap: 12px; }
  .tp-tools-line { display: block; margin: 8px 0 0; }
  .tp-closing { padding: 80px 0 96px; }
}
@media (prefers-reduced-motion: reduce) {
  .tp-dot.on { animation: none; }
  .tp-works img, .tp-work-meta { transition: none; }
}
</style>
